import { NavLink, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Icon } from './Icon.jsx';
import { featureRoutes } from '../app/router.jsx';
import { useAuth } from '../shared/useAuth.js';

const groupOrder = ['main', 'operations', 'sales', 'inventory', 'finance', 'admin'];
const groupLabels = {
  main: 'الرئيسية',
  operations: 'التشغيل',
  sales: 'المبيعات',
  inventory: 'المخزون',
  finance: 'المالية',
  admin: 'الإدارة'
};

function buildNav(routes, hasPermission) {
  const items = routes
    .filter((r) => r.sidebar)
    .map((r) => ({
      key: r.folder || r.path,
      path: r.path,
      label: r.sidebar.label,
      icon: r.sidebar.icon || 'dashboard',
      group: r.sidebar.group || 'main',
      badge: r.sidebar.badge,
      permission: r.permission,
      anyPermission: r.anyPermission,
      order: r.sidebar.order ?? 100
    }))
    .filter((it) => {
      if (it.anyPermission && it.anyPermission.length) {
        return it.anyPermission.some((p) => hasPermission(p));
      }
      return !it.permission || hasPermission(it.permission);
    })
    .sort((a, b) => a.order - b.order);

  const grouped = {};
  for (const it of items) {
    grouped[it.group] = grouped[it.group] || [];
    grouped[it.group].push(it);
  }
  return groupOrder.filter((g) => grouped[g]).map((g) => ({ group: g, items: grouped[g] }));
}

export function Sidebar() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const permissions = useAuth((s) => s.permissions);
  const hasPermission = useAuth((s) => s.hasPermission);
  const logout = useAuth((s) => s.logout);

  const groups = buildNav(featureRoutes, hasPermission);
  const companyName = useAuth((s) => (s.company ? s.company.name : ''));
  const onLogout = () => logout().then(() => navigate('/login', { replace: true }));

  return (
    <aside
      className="text-white sticky top-0 h-screen flex flex-col gap-1 px-4 py-5"
      style={{ background: 'linear-gradient(180deg, #1A1B1E 0%, #0F1012 100%)', width: 248 }}
    >
      <div className="flex items-center gap-3 px-2 pb-4 mb-3 border-b border-white/10">
        <img
          src="/kidzo-mark.png"
          alt="Kids Zone"
          className="w-12 h-12 shrink-0 object-contain"
          style={{ filter: 'drop-shadow(0 4px 10px rgba(255,122,26,.35))' }}
        />
        <div>
          <div className="font-bold text-[17px] tracking-tight">Kids Zone</div>
          <div className="text-[11px] text-white/55">{companyName || 'Kids Zone POS'}</div>
        </div>
      </div>

      <nav className="flex flex-col gap-0.5 flex-1 overflow-y-auto">
        {groups.length === 0 ? (
          <div className="text-white/50 text-[12px] px-3 py-4">لم يتم تحميل صفحات بعد</div>
        ) : null}
        {groups.map(({ group, items }) => (
          <div key={group}>
            <div className="text-[10.5px] font-semibold text-white/40 uppercase tracking-wider px-2.5 pt-3 pb-1.5">
              {groupLabels[group] || group}
            </div>
            {items.map((it) => (
              <NavLink
                key={it.key}
                to={it.path}
                end={it.path === '/'}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[13.5px] font-medium transition-colors',
                    isActive
                      ? 'text-white'
                      : 'text-white/75 hover:bg-white/5 hover:text-white'
                  )
                }
                style={({ isActive }) =>
                  isActive
                    ? {
                        background: 'linear-gradient(90deg, rgba(255,122,26,.18), rgba(255,122,26,0))',
                        boxShadow: 'inset 3px 0 0 #FF7A1A'
                      }
                    : undefined
                }
              >
                <Icon name={it.icon} size={18} />
                <span className="flex-1 text-right">{it.label}</span>
                {it.badge ? (
                  <span className="bg-orange text-white text-[10.5px] font-bold px-2 rounded-full">{it.badge}</span>
                ) : null}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {user ? (
        <div className="mt-auto flex items-center gap-2.5 p-2.5 rounded-[12px] bg-white/5 border border-white/10">
          <div
            className="w-[34px] h-[34px] rounded-full grid place-items-center font-bold text-white text-[13px]"
            style={{ background: 'linear-gradient(135deg,#06B6D4,#0EA5E9)' }}
          >
            {(user.fullName || user.username || '?')[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold truncate">{user.fullName || user.username}</div>
            <div className="text-[11px] text-white/55 truncate">
              {Array.isArray(permissions) && permissions.includes('*') ? 'مدير عام' : (user.roleName || 'مستخدم')}
            </div>
          </div>
          <button
            type="button"
            className="text-white/60 hover:text-white"
            onClick={onLogout}
            title="تسجيل خروج"
          >
            <Icon name="logout" size={16} />
          </button>
        </div>
      ) : null}
    </aside>
  );
}
